import { Check, Loader2, Plus, RefreshCw, Send } from "lucide-react";
import { Modal } from "../../components/ui/modal";
import { cn } from "../../lib/utils";

export type PublishMode = "new_version" | "overwrite";

interface PublishVersionModalProps {
  entityLabel: string;
  mode: PublishMode;
  onModeChange: (mode: PublishMode) => void;
  newVersionName: string;
  onNewVersionNameChange: (value: string) => void;
  overwriteVersionName: string;
  onOverwriteVersionNameChange: (value: string) => void;
  canOverwrite: boolean;
  overwriteDisabledReason?: string;
  publishing: boolean;
  onClose: () => void;
  onPublish: (mode: PublishMode, versionName: string) => void;
}

export function PublishVersionModal({
  entityLabel,
  mode,
  onModeChange,
  newVersionName,
  onNewVersionNameChange,
  overwriteVersionName,
  onOverwriteVersionNameChange,
  canOverwrite,
  overwriteDisabledReason,
  publishing,
  onClose,
  onPublish,
}: PublishVersionModalProps) {
  const selectedName = mode === "overwrite" ? overwriteVersionName : newVersionName;

  return (
    <Modal
      title={`Publish ${entityLabel}`}
      subtitle="Choose how this configuration should be stored."
      onClose={() => {
        if (!publishing) onClose();
      }}
      width="max-w-3xl"
      footer={(
        <>
          <button
            type="button"
            onClick={onClose}
            disabled={publishing}
            className="rounded-md border border-line bg-elevated px-4 py-2 text-xs font-bold text-ink hover:bg-soft disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onPublish(mode, selectedName)}
            disabled={publishing || !selectedName.trim()}
            className="inline-flex items-center gap-2 rounded-md bg-brand px-4 py-2 text-xs font-bold text-white hover:bg-brand-hover shadow-xs transition-colors disabled:cursor-not-allowed disabled:opacity-40"
          >
            {publishing
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Send className="h-3.5 w-3.5" />}
            Publish
          </button>
        </>
      )}
    >
      <div className="grid gap-3 md:grid-cols-2">
        <button
          type="button"
          onClick={() => onModeChange("new_version")}
          className={cn(
            "min-h-44 rounded-md border p-4 text-left transition-colors",
            mode === "new_version"
              ? "border-brand bg-brand/10 dark:bg-brand/20"
              : "border-line bg-elevated hover:bg-soft",
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-soft text-ink-2">
              <Plus className="h-4 w-4" />
            </span>
            <span className={cn(
              "flex h-5 w-5 items-center justify-center rounded-full border",
              mode === "new_version"
                ? "border-brand bg-brand text-white"
                : "border-line bg-elevated",
            )}>
              {mode === "new_version" && <Check className="h-3 w-3" />}
            </span>
          </div>
          <h3 className="mt-4 text-sm font-bold text-ink">Create new version</h3>
          <p className="mt-1 text-xs leading-5 text-ink-2">
            Preserve the existing version and publish this configuration as a new one.
          </p>
        </button>

        <button
          type="button"
          disabled={!canOverwrite}
          onClick={() => onModeChange("overwrite")}
          className={cn(
            "min-h-44 rounded-md border p-4 text-left transition-colors",
            mode === "overwrite"
              ? "border-warning bg-warning-soft/30 dark:bg-warning/20"
              : "border-line bg-elevated hover:bg-soft",
            !canOverwrite && "cursor-not-allowed opacity-50",
          )}
        >
          <div className="flex items-start justify-between gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-soft text-ink-2">
              <RefreshCw className="h-4 w-4" />
            </span>
            <span className={cn(
              "flex h-5 w-5 items-center justify-center rounded-full border",
              mode === "overwrite"
                ? "border-warning bg-warning text-white"
                : "border-line bg-elevated",
            )}>
              {mode === "overwrite" && <Check className="h-3 w-3" />}
            </span>
          </div>
          <h3 className="mt-4 text-sm font-bold text-ink">Overwrite version</h3>
          <p className="mt-1 text-xs leading-5 text-ink-2">
            {canOverwrite
              ? "Replace the selected version without adding another version."
              : overwriteDisabledReason || "No stored version is available to overwrite."}
          </p>
        </button>
      </div>

      <div className="mt-5 grid gap-4 border-t border-line pt-5 md:grid-cols-2">
        <label className={cn("block", mode !== "new_version" && "opacity-60")}>
          <span className="mb-1.5 block text-xs font-bold text-ink">New version name</span>
          <input
            value={newVersionName}
            onFocus={() => onModeChange("new_version")}
            onChange={(event) => onNewVersionNameChange(event.target.value)}
            placeholder="Version 2"
            className="h-10 w-full rounded-md border border-line bg-canvas px-3 text-sm text-ink outline-none focus:border-brand"
          />
        </label>
        <label className={cn("block", (mode !== "overwrite" || !canOverwrite) && "opacity-60")}>
          <span className="mb-1.5 block text-xs font-bold text-ink">Overwrite version name</span>
          <input
            value={overwriteVersionName}
            disabled={!canOverwrite}
            onFocus={() => onModeChange("overwrite")}
            onChange={(event) => onOverwriteVersionNameChange(event.target.value)}
            placeholder="Current version"
            className="h-10 w-full rounded-md border border-line bg-canvas px-3 text-sm text-ink outline-none focus:border-warning disabled:cursor-not-allowed"
          />
        </label>
      </div>
    </Modal>
  );
}
